declare const __APP_SENTRY_RELEASE__: string | undefined;
declare const __APP_TRADE_DIAGNOSTICS__: boolean | undefined;

/** Route and funding diagnostics are restricted to local/test and staging. */
export function isTradeDiagnosticsEnabled(): boolean {
  return (
    import.meta.env.DEV ||
    import.meta.env.MODE === "test" ||
    (typeof __APP_TRADE_DIAGNOSTICS__ !== "undefined" &&
      __APP_TRADE_DIAGNOSTICS__ === true)
  );
}

export const DEFAULT_CENTRAL_LICENSE_KEY =
  "691B62EF-4CB2-42A5-9C49-9700387C4E8D";
export const DEFAULT_VITEST_CENTRAL_LICENSE_KEY = DEFAULT_CENTRAL_LICENSE_KEY;
export const DEFAULT_POSTHOG_HOST = "https://e.glider.fi";

const MAINNET_RPC_ENV_NAMES = [
  "RPC_EIP155_1_HTTPS",
  "VITE_JSON_RPC_URL_1",
  "NEXT_PUBLIC_JSON_RPC_URL_1",
] as const;

const BASE_RPC_ENV_NAMES = [
  "RPC_EIP155_8453_HTTPS",
  "VITE_JSON_RPC_URL_8453",
  "NEXT_PUBLIC_JSON_RPC_URL_8453",
] as const;

const ARBITRUM_RPC_ENV_NAMES = [
  "RPC_EIP155_42161_HTTPS",
  "VITE_JSON_RPC_URL_42161",
  "NEXT_PUBLIC_JSON_RPC_URL_42161",
] as const;

const BSC_RPC_ENV_NAMES = [
  "RPC_EIP155_56_HTTPS",
  "VITE_JSON_RPC_URL_56",
  "NEXT_PUBLIC_JSON_RPC_URL_56",
] as const;

const ROBINHOOD_RPC_ENV_NAMES = [
  "RPC_EIP155_4663_HTTPS",
  "VITE_JSON_RPC_URL_4663",
  "NEXT_PUBLIC_JSON_RPC_URL_4663",
] as const;

const HYPEREVM_RPC_ENV_NAMES = [
  "RPC_EIP155_999_HTTPS",
  "VITE_JSON_RPC_URL_999",
  "NEXT_PUBLIC_JSON_RPC_URL_999",
] as const;

const MONAD_RPC_ENV_NAMES = [
  "VITE_JSON_RPC_URL_143",
  "NEXT_PUBLIC_JSON_RPC_URL_143",
] as const;

const SOLANA_MAINNET_RPC_ENV_NAMES = [
  "RPC_SOLANA_MAINNET_HTTPS",
  "RPC_SOLANA_HTTPS",
  "VITE_SOLANA_MAINNET_RPC_URL",
] as const;

const SOLANA_MAINNET_WSS_ENV_NAMES = [
  "RPC_SOLANA_MAINNET_WSS",
  "RPC_SOLANA_WSS",
  "VITE_SOLANA_MAINNET_WSS_URL",
] as const;

function readStringEnv(...names: string[]): string | undefined {
  for (const name of names) {
    const value = (import.meta.env as Record<string, string | undefined>)[name];
    const normalized = value?.trim();

    if (normalized) {
      return normalized;
    }
  }

  return undefined;
}

function readBooleanEnv(...names: string[]): boolean | undefined {
  const raw = readStringEnv(...names);
  if (!raw) {
    return undefined;
  }

  const normalized = raw.toLowerCase();
  if (normalized === "1" || normalized === "true" || normalized === "yes") {
    return true;
  }
  if (normalized === "0" || normalized === "false" || normalized === "no") {
    return false;
  }

  return undefined;
}

function readNumberEnv(...names: string[]): number | undefined {
  const raw = readStringEnv(...names);
  if (!raw) {
    return undefined;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  return parsed;
}

export function resolvePlatformApiBaseUrl(input: {
  configuredUrl?: string;
  isDev: boolean;
}): string {
  if (input.configuredUrl) {
    return input.configuredUrl;
  }

  // In local Vite dev, use a same-origin base path and let Vite proxy to the
  // remote backend to avoid browser CORS blocks.
  if (input.isDev) {
    return "";
  }

  throw new Error(
    "Missing required `VITE_PLATFORM_API_URL` for non-development webapp-v2 runtime.",
  );
}

export function getPlatformApiBaseUrl(): string {
  return resolvePlatformApiBaseUrl({
    configuredUrl: readStringEnv("VITE_PLATFORM_API_URL"),
    isDev: import.meta.env.DEV,
  });
}

export function resolveExecutionEngineBaseUrl(input: {
  configuredUrl?: string;
  isDev: boolean;
  localDevBaseUrl?: string | null;
}): string {
  if (input.configuredUrl) {
    return input.configuredUrl.replace(/\/+$/u, "");
  }

  if (input.isDev) {
    const localBaseUrl = input.localDevBaseUrl?.trim();
    return (
      localBaseUrl && localBaseUrl.length > 0
        ? localBaseUrl
        : "http://localhost:3000"
    ).replace(/\/+$/u, "");
  }

  throw new Error(
    "Missing required `VITE_EXECUTION_ENGINE_URL` for non-development webapp-v2 runtime.",
  );
}

function getBrowserOrigin(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.location.origin;
}

export function getExecutionEngineBaseUrl(): string {
  return resolveExecutionEngineBaseUrl({
    configuredUrl: readStringEnv("VITE_EXECUTION_ENGINE_URL"),
    isDev: import.meta.env.DEV || import.meta.env.MODE === "test",
    localDevBaseUrl: getBrowserOrigin(),
  });
}

export function getExecutionEngineRequestTimeoutMs(): number | null {
  const configured = readNumberEnv("VITE_EXECUTION_ENGINE_REQUEST_TIMEOUT_MS");
  if (configured === undefined || configured <= 0) {
    return null;
  }

  return configured;
}

export function resolveAgentControlPlaneHttpBaseUrl(input: {
  configuredUrl?: string;
  isDev: boolean;
}): string {
  if (input.configuredUrl) {
    return input.configuredUrl.replace(/\/+$/u, "");
  }

  if (input.isDev) {
    return "";
  }

  throw new Error(
    "Missing required `VITE_AGENT_CONTROL_PLANE_URL` for non-development webapp-v2 runtime.",
  );
}

export function getAgentControlPlaneHttpBaseUrl(): string {
  return resolveAgentControlPlaneHttpBaseUrl({
    configuredUrl: readStringEnv("VITE_AGENT_CONTROL_PLANE_URL"),
    isDev: import.meta.env.DEV || import.meta.env.MODE === "test",
  });
}

export interface AgentControlPlaneConnectionTarget {
  readonly basePathPrefix: string;
  readonly host: string | undefined;
  readonly protocol: "ws" | "wss" | undefined;
}

export function resolveAgentControlPlaneConnectionTarget(input: {
  configuredUrl?: string;
  isDev: boolean;
}): AgentControlPlaneConnectionTarget {
  if (!input.configuredUrl) {
    if (!input.isDev) {
      throw new Error(
        "Missing required `VITE_AGENT_CONTROL_PLANE_URL` for non-development webapp-v2 runtime.",
      );
    }

    return {
      basePathPrefix: "",
      host: undefined,
      protocol: undefined,
    };
  }

  const parsedUrl = new URL(input.configuredUrl);
  return {
    basePathPrefix: parsedUrl.pathname.replace(/\/+$/u, ""),
    host: parsedUrl.host,
    protocol: parsedUrl.protocol === "https:" ? "wss" : "ws",
  };
}

export function getAgentControlPlaneConnectionTarget(): AgentControlPlaneConnectionTarget {
  return resolveAgentControlPlaneConnectionTarget({
    configuredUrl: readStringEnv("VITE_AGENT_CONTROL_PLANE_URL"),
    isDev: import.meta.env.DEV || import.meta.env.MODE === "test",
  });
}

export function getAgentControlPlanePresentedUserIdOverride(): string | null {
  return readStringEnv("VITE_AGENT_CONTROL_PLANE_PRESENTED_USER_ID") ?? null;
}

/**
 * Runtime facts behind the trade ticket's mock order flow. The flow is a
 * development seam; `VITE_ENABLE_MARKET_TRADE_MOCK_FLOW=1` is the explicit
 * staging/test-only opt-in for non-development builds and must never be set in
 * a production build.
 */
export function getMarketTradeMockFlowEnv(): {
  readonly explicitOptIn: boolean;
  readonly isDevelopmentRuntime: boolean;
} {
  return {
    explicitOptIn: readStringEnv("VITE_ENABLE_MARKET_TRADE_MOCK_FLOW") === "1",
    isDevelopmentRuntime:
      import.meta.env.DEV || import.meta.env.MODE === "test",
  };
}

export function resolvePublicAssetUrl(input: {
  assetPath: string;
  configuredBaseUrl?: string;
}): string {
  const normalizedAssetPath = input.assetPath.startsWith("/")
    ? input.assetPath
    : `/${input.assetPath}`;

  if (!input.configuredBaseUrl) {
    return normalizedAssetPath;
  }

  const normalizedBaseUrl = input.configuredBaseUrl.replace(/\/+$/, "");
  return `${normalizedBaseUrl}${normalizedAssetPath}`;
}

export function getPublicAssetBaseUrl(): string | null {
  return readStringEnv("VITE_PUBLIC_ASSET_BASE_URL") ?? null;
}

/**
 * Base URL of the glider-icons worker (first-party icon CDN). Unset or empty
 * (prod today) means callers keep building legacy platform-api branding URLs.
 */
export function getIconsBaseUrl(): string | null {
  return readStringEnv("VITE_ICONS_BASE_URL") ?? null;
}

export function getPublicAssetUrl(assetPath: string): string {
  return resolvePublicAssetUrl({
    assetPath,
    configuredBaseUrl: getPublicAssetBaseUrl() ?? undefined,
  });
}

export function resolveBaseBoostedStrategyHref(
  blueprintId: string | null | undefined,
): string | null {
  const normalizedBlueprintId = blueprintId?.trim();
  if (!normalizedBlueprintId) {
    return null;
  }

  return `/strategy/${encodeURIComponent(normalizedBlueprintId)}`;
}

export function getBaseBoostedBlueprintId(): string | null {
  return readStringEnv("VITE_BASE_BOOSTED_BLUEPRINT_ID") ?? null;
}

export function getBaseBoostedStrategyHref(): string | null {
  return resolveBaseBoostedStrategyHref(getBaseBoostedBlueprintId());
}

export function getPrivyAppId(): string | null {
  const rawValue = readStringEnv("VITE_PRIVY_APP_ID");
  if (!rawValue) {
    return null;
  }

  const normalizedValue = rawValue.split(/\s+#/u, 1)[0]?.trim() ?? "";
  if (!/^cm[a-z0-9]+$/u.test(normalizedValue)) {
    return null;
  }

  return normalizedValue;
}

export function getWalletConnectProjectId(): string | undefined {
  return readStringEnv("VITE_WALLETCONNECT_PROJECT_ID");
}

export function getCentralLicenseKey(): string {
  return (
    readStringEnv("VITE_CENTRAL_LICENSE_KEY", "CENTRAL_LICENSE_KEY") ??
    DEFAULT_CENTRAL_LICENSE_KEY
  );
}

export function getVitestCentralLicenseKey(): string {
  return (
    readStringEnv("VITEST_CENTRAL_LICENSE_KEY") ??
    DEFAULT_VITEST_CENTRAL_LICENSE_KEY
  );
}

export function getSentryDsn(): string | null {
  return readStringEnv("VITE_SENTRY_DSN") ?? null;
}

export function getSentryEnvironment(): string {
  return readStringEnv("VITE_SENTRY_ENVIRONMENT") ?? import.meta.env.MODE;
}

export function getSentryRelease(): string | undefined {
  const configuredRelease = readStringEnv("VITE_SENTRY_RELEASE");
  if (configuredRelease) {
    return configuredRelease;
  }

  const bundledRelease = __APP_SENTRY_RELEASE__?.trim();
  return bundledRelease ? bundledRelease : undefined;
}

export function getSentryTracesSampleRate(): number {
  const configuredRate = readNumberEnv("VITE_SENTRY_TRACES_SAMPLE_RATE");
  if (
    configuredRate !== undefined &&
    configuredRate >= 0 &&
    configuredRate <= 1
  ) {
    return configuredRate;
  }

  return 0.1;
}

export function getPostHogKey(): string | null {
  return readStringEnv("VITE_POSTHOG_KEY") ?? null;
}

export function getPostHogHost(): string {
  return readStringEnv("VITE_POSTHOG_HOST") ?? DEFAULT_POSTHOG_HOST;
}

export function getGaMeasurementId(): string | null {
  return readStringEnv("VITE_GA_MEASUREMENT_ID") ?? null;
}

/**
 * Crisp workspace behind in-app support chat. The id is public by design (it
 * ships in the client bundle); unset means the support entry falls back to the
 * public support page.
 */
export function getCrispWebsiteId(): string | null {
  return readStringEnv("VITE_CRISP_WEBSITE_ID") ?? null;
}

export function isWebappV2FrontendTelemetryEnabled(): boolean {
  return readBooleanEnv("VITE_ENABLE_WEBAPP_V2_FRONTEND_TELEMETRY") ?? false;
}

export function isWebappV2ProductAnalyticsEnabled(): boolean {
  return readBooleanEnv("VITE_ENABLE_WEBAPP_V2_PRODUCT_ANALYTICS") ?? false;
}

export function isWebappV2SessionReplayEnabled(): boolean {
  return readBooleanEnv("VITE_ENABLE_WEBAPP_V2_SESSION_REPLAY") ?? false;
}

export function getMarketDataStreamBaseUrl(): string | null {
  return readStringEnv("VITE_MARKET_DATA_STREAM_URL") ?? null;
}

/** Onramper widget public API key; its presence enables the cash rails. */
export function getOnramperApiKey(): string | null {
  return readStringEnv("VITE_ONRAMPER_API_KEY") ?? null;
}

/**
 * Kill switch for the geo-specific deposit options (country profiles,
 * selector, two-page cash tab). Defaults ON; only an explicit `false`
 * disables — legacy-webapp parity (`NEXT_PUBLIC_DEPOSIT_GEO_OPTIONS_ENABLED`).
 */
export function getIsDepositGeoOptionsEnabled(): boolean {
  return readBooleanEnv("VITE_DEPOSIT_GEO_OPTIONS_ENABLED") !== false;
}

/**
 * Kill switch for onramp availability beyond Base/BNB EVM primaries.
 * Defaults ON; explicit `false` collapses non-Base/BNB EVM portfolio
 * deposits to crypto-only — legacy-webapp parity
 * (`NEXT_PUBLIC_ONRAMP_CHAIN_EXPANSION_ENABLED`).
 */
export function getIsOnrampChainExpansionEnabled(): boolean {
  return readBooleanEnv("VITE_ONRAMP_CHAIN_EXPANSION_ENABLED") !== false;
}

/** Client-side visibility switches for the exchange onramp tiles. */
export function getOnrampClientEnv(): {
  binanceConnectEnabled: boolean;
  onrampChainExpansionEnabled: boolean;
  onramperEnabled: boolean;
  robinhoodConnectEnabled: boolean;
} {
  return {
    binanceConnectEnabled:
      readBooleanEnv("VITE_BINANCE_CONNECT_ENABLED") ?? false,
    onrampChainExpansionEnabled: getIsOnrampChainExpansionEnabled(),
    onramperEnabled: getOnramperApiKey() !== null,
    robinhoodConnectEnabled:
      readBooleanEnv("VITE_ROBINHOOD_CONNECT_ENABLED") ?? false,
  };
}

export function getSolanaMainnetRpcUrl(): string {
  return (
    readStringEnv(...SOLANA_MAINNET_RPC_ENV_NAMES) ??
    "https://api.mainnet-beta.solana.com"
  );
}

export function getSolanaMainnetWssUrl(): string {
  return (
    readStringEnv(...SOLANA_MAINNET_WSS_ENV_NAMES) ??
    "wss://api.mainnet-beta.solana.com"
  );
}

export function getRpcUrl(
  chainId: 1 | 56 | 143 | 8453 | 42161 | 4663 | 999,
): string {
  const configuredUrl = readStringEnv(
    ...(chainId === 1
      ? MAINNET_RPC_ENV_NAMES
      : chainId === 56
        ? BSC_RPC_ENV_NAMES
        : chainId === 143
          ? MONAD_RPC_ENV_NAMES
          : chainId === 42161
            ? ARBITRUM_RPC_ENV_NAMES
            : chainId === 4663
              ? ROBINHOOD_RPC_ENV_NAMES
              : chainId === 999
                ? HYPEREVM_RPC_ENV_NAMES
                : BASE_RPC_ENV_NAMES),
  );

  if (configuredUrl) {
    return configuredUrl;
  }

  if (chainId === 1) {
    return "https://ethereum.publicnode.com";
  }

  if (chainId === 56) {
    return "https://bsc-dataseed.bnbchain.org";
  }

  if (chainId === 143) {
    return "https://rpc.monad.xyz";
  }

  if (chainId === 42161) {
    return "https://arb1.arbitrum.io/rpc";
  }

  if (chainId === 4663) {
    return "https://robinhoodchain.blockscout.com/api/eth-rpc";
  }

  if (chainId === 999) {
    return "https://rpc.hyperliquid.xyz/evm";
  }

  return "https://mainnet.base.org";
}
